import { useState } from 'react';

import { api } from '../api/client';
import { Badge, Button, Card, Field, Form, Loading, Modal, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * Rule authoring, with a dry run before anything goes live.
 *
 * The dry run is the part that matters. A rule that turns out to block a third of ordinary
 * work is a discovery worth making against last month's submissions rather than against
 * tomorrow's field users — and because the engine is a pure function, replaying it over
 * history is exactly the computation that would run live, not an approximation of it.
 *
 * Published versions are frozen for the same reason workflow versions are: findings cite
 * the version that produced them, so editing one in place would rewrite the basis of
 * decisions already taken.
 */

interface Condition {
  id: string;
  subject: string;
  subjectKey: string;
  operator: string;
  valueText: string | null;
  valueNumber: string | null;
  valueBool: boolean | null;
  valueList: string[] | null;
}

interface RuleVersion {
  id: string;
  versionNumber: number;
  action: string;
  matchMode: string;
  message: string;
  isPublished: boolean;
  publishedAt: string | null;
  conditions: Condition[];
}

interface RuleDetail {
  id: string;
  key: string;
  name: string;
  description: string | null;
  evaluationOrder: number;
  isActive: boolean;
  versions: RuleVersion[];
}

/** A question a rule can be written against — named, and with the options it offers. */
interface AnswerSubject {
  key: string;
  prompt: string;
  formName: string;
  stepType: string;
  options: Array<{ value: string; label: string }>;
}

interface Subjects {
  answers: AnswerSubject[];
  siteAttributes: string[];
  validationChecks: string[];
  submissionFields: string[];
}

interface DryRunResult {
  evaluated: number;
  matched: number;
  action: string;
  samples: Array<{ submissionId: string; siteCode: string; explanation: string; currentVerdict: string | null }>;
}

const ACTIONS = [
  { value: 'APPROVE', label: 'Approve — record it, change nothing' },
  { value: 'ROUTE', label: 'Route — send for human review' },
  { value: 'BLOCK', label: 'Block — refuse the submission' },
  { value: 'PREVENT', label: 'Prevent — stop before work begins' },
];

const OPERATORS = [
  { value: 'EQUALS', label: 'equals' },
  { value: 'NOT_EQUALS', label: 'does not equal' },
  { value: 'GREATER_THAN', label: 'is greater than' },
  { value: 'GREATER_OR_EQUAL', label: 'is at least' },
  { value: 'LESS_THAN', label: 'is less than' },
  { value: 'LESS_OR_EQUAL', label: 'is at most' },
  { value: 'CONTAINS', label: 'contains' },
  { value: 'IS_NULL', label: 'is not answered' },
  { value: 'IS_NOT_NULL', label: 'is answered' },
];

export function Rules(): JSX.Element {
  const { can } = useSession();
  const rules = useResource(() => api.get<RuleDetail[]>('/rules'), []);
  const subjects = useResource(() => api.get<Subjects>('/rules/subjects'), []);
  const { run, error, busy } = useAction();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<{ rule: RuleDetail; version: RuleVersion } | null>(null);
  const [dryRun, setDryRun] = useState<{ version: RuleVersion; result: DryRunResult } | null>(null);

  const runDryRun = async (version: RuleVersion): Promise<void> => {
    await run(async () => {
      const result = await api.post<DryRunResult>(`/rule-versions/${version.id}/dry-run`, { limit: 100 });
      setDryRun({ version, result });
    });
  };

  const publish = async (version: RuleVersion): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/rule-versions/${version.id}/publish`);
    });
    if (ok) rules.reload();
  };

  const fork = async (version: RuleVersion, rule: RuleDetail): Promise<void> => {
    await run(async () => {
      const created = await api.post<{ id: string }>(`/rule-versions/${version.id}/draft`);
      rules.reload();
      setEditing({ rule, version: { ...version, id: created.id, isPublished: false, conditions: version.conditions } });
    });
  };

  return (
    <>
      {/* Not authorisation — the server refuses regardless. This is so a reader without
          the permission is not offered a button whose only outcome is a refusal. */}
      <Card
        title="Rules"
        actions={can('rule.edit') ? <Button onClick={() => setCreating(true)}>New rule</Button> : undefined}
      >
        <p className="muted">
          Rules run after the deterministic checks, so a rule can read a check's result. A
          rule can only make a verdict harsher, never softer — an approving rule cannot
          overturn a failed geofence check.
        </p>

        {error !== null && <Notice kind="error">{error}</Notice>}

        {rules.loading ? (
          <Loading />
        ) : rules.error !== null ? (
          <Notice kind="error">{rules.error}</Notice>
        ) : (
          <Table
            rows={rules.data ?? []}
            keyOf={(row) => row.id}
            empty="No rules yet. The deterministic checks still run without them."
            columns={[
              { header: 'Rule', render: (row) => row.name },
              { header: 'Key', render: (row) => <code>{row.key}</code> },
              { header: 'Order', numeric: true, render: (row) => row.evaluationOrder },
              {
                header: 'Versions',
                render: (row) => (
                  <div className="row">
                    {row.versions.map((version) => (
                      <Badge key={version.id} tone={version.isPublished ? 'accepted' : 'flagged'}>
                        v{version.versionNumber} · {version.isPublished ? 'live' : 'draft'} ·{' '}
                        {version.conditions.length} condition(s)
                      </Badge>
                    ))}
                  </div>
                ),
              },
              {
                header: 'Status',
                render: (row) =>
                  row.isActive ? <Badge tone="accepted">Active</Badge> : <Badge tone="blocked">Inactive</Badge>,
              },
              {
                header: '',
                render: (row) => {
                  const draft = row.versions.find((version) => !version.isPublished);
                  const live = row.versions.find((version) => version.isPublished);

                  return (
                    <div className="row">
                      {draft !== undefined && (
                        <>
                          <Button variant="secondary" onClick={() => setEditing({ rule: row, version: draft })}>
                            Edit draft
                          </Button>
                          <Button variant="secondary" onClick={() => void runDryRun(draft)} disabled={busy}>
                            Dry run
                          </Button>
                          <Button onClick={() => void publish(draft)} disabled={busy || draft.conditions.length === 0}>
                            Publish
                          </Button>
                        </>
                      )}
                      {draft === undefined && live !== undefined && (
                        <Button variant="secondary" onClick={() => void fork(live, row)} disabled={busy}>
                          New version
                        </Button>
                      )}
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </Card>

      {creating && (
        <NewRuleForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            rules.reload();
          }}
        />
      )}

      {editing !== null && (
        <ConditionEditor
          rule={editing.rule}
          version={editing.version}
          subjects={subjects.data}
          onClose={() => setEditing(null)}
          onChanged={() => {
            rules.reload();
          }}
        />
      )}

      {dryRun !== null && (
        <Modal title="Dry run" onClose={() => setDryRun(null)}>
          <div className="stack">
            <Notice kind={dryRun.result.matched === 0 ? 'info' : 'error'}>
              This rule would have fired on <strong>{dryRun.result.matched}</strong> of the last{' '}
              {dryRun.result.evaluated} submission(s), with the action{' '}
              <strong>{dryRun.result.action}</strong>.
            </Notice>

            {dryRun.result.matched === 0 && (
              <p className="muted">
                Nothing matched. That may be correct, or it may mean the subject key does not
                appear in any workflow — a rule about a field nobody records never fires.
              </p>
            )}

            <Table
              rows={dryRun.result.samples}
              keyOf={(row) => row.submissionId}
              empty="No examples to show."
              columns={[
                { header: 'Site', render: (row) => row.siteCode },
                { header: 'Current verdict', render: (row) => <Badge tone={row.currentVerdict ?? 'low'}>{row.currentVerdict ?? '—'}</Badge> },
                { header: 'Would fire because', render: (row) => row.explanation },
              ]}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

function NewRuleForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }): JSX.Element {
  const { run, error, busy } = useAction();
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [action, setAction] = useState('ROUTE');
  const [matchMode, setMatchMode] = useState('ALL');
  const [message, setMessage] = useState('');
  const [order, setOrder] = useState('100');

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/rules', {
        key,
        name,
        action,
        matchMode,
        message,
        evaluationOrder: Number(order),
      });
    });
    if (ok) onCreated();
  };

  return (
    <Modal title="New rule" onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Field label="Key" value={key} onChange={setKey} required hint="Stable identifier, e.g. cold_chain_breach" />
          <Field label="Name" value={name} onChange={setName} required />
        </div>

        <Select label="Action" value={action} onChange={setAction} required options={ACTIONS} />

        <Select
          label="Fires when"
          value={matchMode}
          onChange={setMatchMode}
          required
          options={[
            { value: 'ALL', label: 'Every condition is met' },
            { value: 'ANY', label: 'Any condition is met' },
          ]}
        />

        <Field
          label="Message"
          value={message}
          onChange={setMessage}
          required
          hint="Shown to the field user when this fires. It must tell them what to do about it."
        />

        <Field
          label="Evaluation order"
          value={order}
          onChange={setOrder}
          type="number"
          hint="Lower runs first."
        />

        <div className="form-actions">
          <Button type="submit" disabled={busy || key === '' || name === '' || message === ''}>
            {busy ? 'Creating…' : 'Create draft'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

function ConditionEditor({
  rule,
  version,
  subjects,
  onClose,
  onChanged,
}: {
  rule: RuleDetail;
  version: RuleVersion;
  subjects: Subjects | null;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [conditions, setConditions] = useState<Condition[]>(version.conditions);

  const [subject, setSubject] = useState('ANSWER');
  const [subjectKey, setSubjectKey] = useState('');
  const [operator, setOperator] = useState('EQUALS');
  const [value, setValue] = useState('');

  // Keys the deployment actually has, so an author picks rather than guesses. A typo in a
  // subject key produces a rule that silently never fires.
  const keyOptions = ((): Array<{ value: string; label: string; group?: string }> => {
    if (subjects === null) return [];

    /*
      A question is offered by its prompt, under the form that asks it.

      The list used to be raw field keys — `fire_exits_clear` — which asks an author to
      recognise a question from its identifier and to remember which form it belongs to.
      Both are in the data. The key still travels as the value, because that is what a rule
      matches on; only what the author reads has changed.
    */
    if (subject === 'ANSWER') {
      return subjects.answers.map((entry) => ({
        value: entry.key,
        label: entry.prompt,
        group: entry.formName,
      }));
    }

    const list =
      subject === 'SITE_ATTRIBUTE'
        ? subjects.siteAttributes
        : subject === 'VALIDATION_OUTCOME'
          ? subjects.validationChecks
          : subjects.submissionFields;
    return list.map((entry) => ({ value: entry, label: entry }));
  })();

  /**
   * The options this question actually offers, where it offers any.
   *
   * A key can appear under more than one form, and a rule matches it wherever it occurs, so
   * the choices are pooled across every question with that key and de-duplicated by stored
   * value. Two forms whose "Yes" means different things is a modelling problem in the forms,
   * not something a picker can paper over.
   */
  const valueOptions = ((): Array<{ value: string; label: string }> => {
    if (subject !== 'ANSWER' || subjects === null || subjectKey === '') return [];

    const byValue = new Map<string, string>();
    for (const entry of subjects.answers) {
      if (entry.key !== subjectKey) continue;
      for (const option of entry.options) byValue.set(option.value, option.label);
    }

    return [...byValue.entries()].map(([value, label]) => ({ value, label }));
  })();

  const needsValue = operator !== 'IS_NULL' && operator !== 'IS_NOT_NULL';

  const add = async (): Promise<void> => {
    const body: Record<string, unknown> = { subject, subjectKey, operator };

    if (needsValue) {
      const asNumber = Number(value);
      // A numeric-looking value goes in the numeric column so comparisons are numeric:
      // as text, "9" is greater than "10".
      if (value !== '' && !Number.isNaN(asNumber)) body['valueNumber'] = asNumber;
      else if (value === 'true' || value === 'false') body['valueBool'] = value === 'true';
      else body['valueText'] = value;
    }

    const ok = await run(async () => {
      const created = await api.post<{ id: string }>(`/rule-versions/${version.id}/conditions`, body);
      setConditions([
        ...conditions,
        {
          id: created.id,
          subject,
          subjectKey,
          operator,
          valueText: typeof body['valueText'] === 'string' ? body['valueText'] : null,
          valueNumber: typeof body['valueNumber'] === 'number' ? String(body['valueNumber']) : null,
          valueBool: typeof body['valueBool'] === 'boolean' ? body['valueBool'] : null,
          valueList: null,
        },
      ]);
    });

    if (ok) {
      setSubjectKey('');
      setValue('');
      onChanged();
    }
  };

  const remove = async (conditionId: string): Promise<void> => {
    const ok = await run(async () => {
      await api.delete(`/rule-conditions/${conditionId}`);
    });
    if (ok) {
      setConditions(conditions.filter((condition) => condition.id !== conditionId));
      onChanged();
    }
  };

  return (
    <Modal title={`${rule.name} — version ${version.versionNumber}`} onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <div className="stack">
        <p className="muted">
          Fires when {version.matchMode === 'ALL' ? 'every' : 'any'} condition below is met,
          and then {version.action.toLowerCase()}s the submission.
        </p>

        <Table
          rows={conditions}
          keyOf={(row) => row.id}
          empty="No conditions yet. A rule with none never fires and cannot be published."
          columns={[
            { header: 'Subject', render: (row) => row.subject.replace('_', ' ').toLowerCase() },
            {
              /*
                The prompt where one is known, the raw key where it is not.

                A condition written against a question deleted since, or against a subject
                that has no prompt at all, still has to render — and falling back to the key
                says something true rather than an empty cell.
              */
              header: 'Question',
              render: (row) => {
                const named = (subjects?.answers ?? []).find(
                  (entry) => entry.key === row.subjectKey,
                );
                return named === undefined ? (
                  <code>{row.subjectKey}</code>
                ) : (
                  <span title={row.subjectKey}>{named.prompt}</span>
                );
              },
            },
            { header: 'Test', render: (row) => OPERATORS.find((o) => o.value === row.operator)?.label ?? row.operator },
            {
              /*
                An option's label where the stored text is one, otherwise the raw value.

                The engine compares the option's stable value, so `NO` is what a condition
                holds — and `NO` is not what anybody calls it. Showing the label with the
                value on hover keeps the table readable without hiding what is compared.
              */
              header: 'Value',
              render: (row) => {
                if (row.valueNumber !== null) return row.valueNumber;
                if (row.valueBool !== null) return row.valueBool ? 'Yes' : 'No';
                if (row.valueText === null) return '—';

                const named = (subjects?.answers ?? []).find(
                  (entry) => entry.key === row.subjectKey,
                );
                const option = named?.options.find((entry) => entry.value === row.valueText);

                return option === undefined ? (
                  row.valueText
                ) : (
                  <span title={option.value}>{option.label}</span>
                );
              },
            },
            {
              header: '',
              render: (row) => (
                <Button variant="danger" onClick={() => void remove(row.id)} disabled={busy}>
                  Remove
                </Button>
              ),
            },
          ]}
        />

        <Form onSubmit={() => void add()}>
          <div className="form-row">
            <Select
              label="Subject"
              value={subject}
              onChange={(next) => {
                setSubject(next);
                setSubjectKey('');
                setValue('');
              }}
              required
              options={[
                { value: 'ANSWER', label: 'An answer' },
                { value: 'SITE_ATTRIBUTE', label: 'A site attribute' },
                { value: 'VALIDATION_OUTCOME', label: 'A check outcome' },
                { value: 'SUBMISSION_FIELD', label: 'A submission field' },
              ]}
            />

            {keyOptions.length > 0 ? (
              <Select
                label="Question"
                value={subjectKey}
                onChange={(next) => {
                  setSubjectKey(next);
                  // The old value belonged to the old question, and silently keeping it is
                  // how a rule ends up comparing one question against another's answer.
                  setValue('');
                }}
                required
                options={keyOptions}
              />
            ) : (
              <Field label="Key" value={subjectKey} onChange={setSubjectKey} required />
            )}

            <Select label="Test" value={operator} onChange={setOperator} required options={OPERATORS} />

            {/*
              Picked, not typed, whenever the question has options.

              A free-text value against a choice question is a trap: the engine compares the
              option's *stable value*, so an author typing the label they see on the form
              writes a rule that silently never fires. The list shows the label and stores
              the value, which is the only pairing that can be got right by reading.

              Still a text box for text, numbers and dates, where there is nothing to pick.
            */}
            {needsValue &&
              (valueOptions.length > 0 ? (
                <Select label="Value" value={value} onChange={setValue} required options={valueOptions} />
              ) : (
                <Field label="Value" value={value} onChange={setValue} required />
              ))}
          </div>

          <div className="form-actions">
            <Button type="submit" disabled={busy || subjectKey === '' || (needsValue && value === '')}>
              Add condition
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
